import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiMenu, FiX, FiSearch, FiUser, FiLogOut, FiEdit, FiSettings, 
  FiPlus, FiBookmark, FiStar, FiTv, FiShoppingBag,
  FiAward, FiHelpCircle
} from 'react-icons/fi';
import { MdSportsEsports } from 'react-icons/md';
import Fuse from 'fuse.js';
import { useAuth } from '../../context/AuthContext';
import Logo from '../Logo/Logo';
import NotificationsDropdown from '../NotificationsDropdown/NotificationsDropdown';
import { CATEGORIES } from '../../utils/constants';
import { postsAPI, usersAPI } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import './Header.css';

const NAV_PAGES = [
  { label: 'Inicio', path: '/' },
  { label: 'Explorar', path: '/explore' },
  { label: 'Biblioteca', path: '/library' },
  { label: 'Magazine', path: '/magazine' },
  { label: 'TV', path: '/tv' },
  { label: 'Tienda', path: '/store' },
  { label: 'Juegos', path: '/games' },
  { label: 'Academia', path: '/academy' },
  { label: 'Favoritos', path: '/favorites' },
  { label: 'Ayuda', path: '/help' }
];

const normalizeRole = (role) => {
  if (!role) return 'USER';
  const r = String(role);
  const map = {
    superadmin: 'SUPERADMIN',
    ceo: 'CEO',
    admin: 'ADMIN',
    graphyra_team: 'GRAPHYRA_TEAM',
    COO: 'COO',
    coo: 'COO',
    head_of_content: 'CONTENT_DIRECTOR',
    content_director: 'CONTENT_DIRECTOR',
    community_lead: 'COMMUNITY_LEAD',
    moderator_senior: 'MODERATOR_SENIOR',
    moderator: 'MODERATOR',
    support: 'SUPPORT',
    engineer: 'ENGINEER',
    official_account: 'OFFICIAL_ACCOUNT'
  };
  return map[r] || r.toUpperCase();
};

const canAccessAdminDashboard = (role) => {
  const nr = normalizeRole(role);
  return [
    'SUPERADMIN',
    'CEO',
    'ADMIN',
    'GRAPHYRA_TEAM',
    'COO',
    'CONTENT_DIRECTOR',
    'COMMUNITY_LEAD',
    'MODERATOR_SENIOR',
    'MODERATOR',
    'SUPPORT',
    'ENGINEER'
  ].includes(nr);
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndexPosts, setSearchIndexPosts] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchUserSuggestions, setSearchUserSuggestions] = useState([]);
  const [searchPageSuggestions, setSearchPageSuggestions] = useState([]);
  const [isSearchIndexLoading, setIsSearchIndexLoading] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Cerrar menú del usuario al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getPostRoute = (post) => {
    const slug = post?.slug || (() => {
      const base = slugify(post?.title) || 'post';
      return post?.id ? `${base}-${post.id}` : base;
    })();
    if (post?.type === 'meme') return `/meme/${slug}`;
    if (post?.type === 'video-horizontal' || post?.type === 'video-reel') return `/video/${slug}`;
    return `/article/${slug}`;
  };

  useEffect(() => {
    let cancelled = false;

    const loadIndex = async () => {
      if (!isSearchOpen) {
        setSearchSuggestions([]);
        setSearchUserSuggestions([]);
        setSearchPageSuggestions([]);
        return;
      }
      if (searchIndexPosts.length > 0 || isSearchIndexLoading) return;

      setIsSearchIndexLoading(true);
      try {
        const res = await postsAPI.getAll({ page: 1, limit: 120 });
        const data = res?.data;
        const nextPosts = Array.isArray(data) ? data : (data?.posts || []);
        if (!cancelled) setSearchIndexPosts(nextPosts);
      } catch (e) {
        // If this fails, the search page still works; we just won't show fuzzy suggestions.
      } finally {
        if (!cancelled) setIsSearchIndexLoading(false);
      }
    };

    loadIndex();
    return () => {
      cancelled = true;
    };
  }, [isSearchOpen, searchIndexPosts.length, isSearchIndexLoading]);

  const pagesFuse = React.useMemo(() => {
    return new Fuse(NAV_PAGES, {
      keys: ['label', 'path'],
      threshold: 0.4,
      ignoreLocation: true,
      minMatchCharLength: 1
    });
  }, []);

  const fuse = React.useMemo(() => {
    if (!searchIndexPosts || searchIndexPosts.length === 0) return null;
    return new Fuse(searchIndexPosts, {
      keys: [
        { name: 'title', weight: 0.6 },
        { name: 'excerpt', weight: 0.2 },
        { name: 'description', weight: 0.2 },
        { name: 'author.name', weight: 0.2 },
        { name: 'keywords', weight: 0.2 },
        { name: 'category', weight: 0.1 }
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1
    });
  }, [searchIndexPosts]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const q = String(searchQuery || '').trim();
    if (!q || q.length < 1 || !fuse) {
      setSearchSuggestions([]);
      return;
    }
    const next = fuse.search(q, { limit: 6 }).map(r => r.item);
    setSearchSuggestions(next);
  }, [searchQuery, fuse, isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const q = String(searchQuery || '').trim();
    if (!q || q.length < 1) {
      setSearchUserSuggestions([]);
      setSearchPageSuggestions([]);
      return;
    }

    const nextPages = pagesFuse.search(q, { limit: 4 }).map(r => r.item);
    setSearchPageSuggestions(nextPages);

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await usersAPI.search(q);
        const data = res?.data;
        const nextUsers = Array.isArray(data) ? data : (data?.users || []);
        if (!cancelled) setSearchUserSuggestions(nextUsers.slice(0, 4));
      } catch (e) {
        if (!cancelled) setSearchUserSuggestions([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchQuery, isSearchOpen, pagesFuse]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="header-logo">
          <Logo size="medium" showText={true} />
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav">
          <Link to="/" className="nav-link">Inicio</Link>
          <Link to="/explore" className="nav-link">Explorar</Link>
          <Link to="/library" className="nav-link">Biblioteca</Link>
          <Link to="/magazine" className="nav-link">Magazine</Link>
          <Link to="/tv" className="nav-link" title="Graphyra TV">
            <FiTv className="nav-icon-inline" />
            <span className="nav-text-desktop">TV</span>
          </Link>
          <Link to="/store" className="nav-link" title="Tienda">
            <FiShoppingBag className="nav-icon-inline" />
            <span className="nav-text-desktop">Tienda</span>
          </Link>
          <Link to="/games" className="nav-link" title="Juegos">
            <MdSportsEsports className="nav-icon-inline" />
            <span className="nav-text-desktop">Juegos</span>
          </Link>
          <Link to="/academy" className="nav-link nav-academy" title="Academia">
            <FiAward className="nav-icon-inline" />
            <span className="nav-text-desktop">Academia</span>
          </Link>
          {isAuthenticated && (
            <Link to="/favorites" className="nav-link nav-favorites" title="Favoritos">
              <FiStar className="nav-icon-inline" />
              <span className="nav-text-desktop">Favoritos</span>
            </Link>
          )}
        </nav>

        {/* Search & Actions */}
        <div className="header-actions">
          <button 
            className="action-btn search-btn"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            aria-label="Buscar"
          >
            <FiSearch />
          </button>

          {isAuthenticated ? (
            <>
              <Link to="/create" className="action-btn create-btn" aria-label="Crear publicación">
                <FiPlus />
              </Link>
              
              <NotificationsDropdown />

              <div className="user-menu" ref={userMenuRef}>
                <button 
                  className="user-avatar"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  {user?.avatar ? (
                    <img src={resolveMediaUrl(user.avatar)} alt={user.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </button>

                {isUserMenuOpen && (
                  <div className="user-dropdown">
                    <div className="user-info">
                      <p className="user-name">{user?.name}</p>
                      <p className="user-email">{user?.email}</p>
                    </div>
                    <div className="dropdown-divider"></div>
                    <Link to={`/${user?.username}`} className="dropdown-item">
                      <FiUser /> Mi Perfil
                    </Link>
                    <Link to="/my-posts" className="dropdown-item">
                      <FiEdit /> Mis Publicaciones
                    </Link>
                    <Link to="/favorites" className="dropdown-item">
                      <FiBookmark /> Guardados
                    </Link>
                    {normalizeRole(user?.hierarchical_role) !== 'SUPERADMIN' && (
                      <Link to="/help" className="dropdown-item">
                        <FiHelpCircle /> Ayuda
                      </Link>
                    )}
                    <Link to="/settings/account" className="dropdown-item">
                      <FiSettings /> Configuración
                    </Link>
                    {canAccessAdminDashboard(user?.hierarchical_role) && (
                      <>
                        <div className="dropdown-divider"></div>
                        <Link to="/admin" className="dropdown-item admin-item">
                          <FiSettings /> Administración
                        </Link>
                      </>
                    )}
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout} className="dropdown-item logout">
                      <FiLogOut /> Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost">
                Iniciar Sesión
              </Link>
              <Link to="/register" className="btn btn-primary">
                Registrarse
              </Link>
            </>
          )}

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menú"
          >
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="search-overlay">
          <div className="search-container">
            <form onSubmit={handleSearch} className="search-form">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
                className="search-input"
              />
              <button 
                type="button" 
                className="search-close"
                onClick={() => setIsSearchOpen(false)}
              >
                <FiX />
              </button>
            </form>

            {(searchSuggestions.length > 0 || searchUserSuggestions.length > 0 || searchPageSuggestions.length > 0) && (
              <div className="search-suggestions" aria-label="Sugerencias">
                {searchPageSuggestions.length > 0 && (
                  <div className="search-suggestion-section">
                    <div className="search-suggestion-section-title">Páginas</div>
                    {searchPageSuggestions.map((p) => (
                      <button
                        key={p.path}
                        type="button"
                        className="search-suggestion"
                        onClick={() => {
                          navigate(p.path);
                          setSearchQuery('');
                          setIsSearchOpen(false);
                        }}
                      >
                        <span className="search-suggestion-title">{p.label}</span>
                        <span className="search-suggestion-meta">Página</span>
                      </button>
                    ))}
                  </div>
                )}

                {searchUserSuggestions.length > 0 && (
                  <div className="search-suggestion-section">
                    <div className="search-suggestion-section-title">Usuarios</div>
                    {searchUserSuggestions.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="search-suggestion"
                        onClick={() => {
                          navigate(`/${u.username || u.id}`);
                          setSearchQuery('');
                          setIsSearchOpen(false);
                        }}
                      >
                        <span className="search-suggestion-title">{u.name || u.username || 'Usuario'}</span>
                        <span className="search-suggestion-meta">@{u.username || u.id}</span>
                      </button>
                    ))}
                  </div>
                )}

                {searchSuggestions.length > 0 && (
                  <div className="search-suggestion-section">
                    <div className="search-suggestion-section-title">Contenido</div>
                {searchSuggestions.map((post) => (
                  <button
                    key={post.id}
                    type="button"
                    className="search-suggestion"
                    onClick={() => {
                      navigate(getPostRoute(post));
                      setSearchQuery('');
                      setIsSearchOpen(false);
                    }}
                  >
                    <span className="search-suggestion-title">{post.title}</span>
                    <span className="search-suggestion-meta">
                      {(post.type === 'meme' && 'Meme') ||
                        ((post.type === 'video-horizontal' || post.type === 'video-reel') && 'Video') ||
                        'Artículo'}
                    </span>
                  </button>
                ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="mobile-menu">
          <nav className="mobile-nav">
            <Link to="/" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              🏠 Inicio
            </Link>
            <Link to="/explore" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              🌍 Explorar
            </Link>
            <Link to="/library" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              📚 Biblioteca
            </Link>
            <Link to="/magazine" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              📰 Magazine
            </Link>
            <Link to="/tv" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              📺 Graphyra TV
            </Link>
            <Link to="/store" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              🛍️ Tienda
            </Link>
            <Link to="/games" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              🎮 Juegos
            </Link>
            <Link to="/academy" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
              🎓 Academia
            </Link>
            {isAuthenticated && (
              <>
                <Link to="/favorites" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  ⭐ Favoritos
                </Link>
                <Link to="/create" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  ➕ Crear Publicación
                </Link>
                <Link to="/profile" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  👤 Mi Perfil
                </Link>
                <Link to="/my-posts" className="mobile-nav-link" onClick={() => setIsMenuOpen(false)}>
                  📝 Mis Publicaciones
                </Link>
              </>
            )}
          </nav>

          <div className="mobile-categories">
            <h3>Categorías</h3>
            {CATEGORIES.map(category => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="mobile-category-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <span className="category-icon">{category.icon}</span>
                {category.name}
              </Link>
            ))}
          </div>

          {!isAuthenticated && (
            <div className="mobile-auth">
              <Link to="/login" className="btn btn-ghost btn-block" onClick={() => setIsMenuOpen(false)}>
                Iniciar Sesión
              </Link>
              <Link to="/register" className="btn btn-primary btn-block" onClick={() => setIsMenuOpen(false)}>
                Registrarse
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
